/**
 * @file lib/git/actor-git-manager.ts
 * @description Actor/Session管理のGitマネージャー
 * @author GFTD Co., Ltd.
 */

import { GitLabClient, GitLabConfig, CreateProjectConfig, CreateBranchConfig } from './gitlab-client';
import { GitEmbeddingSDK, GitEmbeddingConfig } from './git-embedding-sdk';
import { GitConfig } from './git-fs';
import { Document } from '@langchain/core/documents';

/**
 * Actor情報インターフェース
 * GitLabプロジェクトとしてのActor表現
 */
export interface ActorInfo {
  /** GitLab Project ID (数値) - 内部的にはproject_idだが、actor_id概念ではrepository_name(UUID)を使用 */
  id: number;
  /** Repository name with UUID (actor_id概念での実際の識別子) */
  name: string;
  description?: string;
  clone_url: string;
  web_url: string;
  default_branch: string;
  created_at: string;
  updated_at?: string;
}

/**
 * Session情報インターフェース
 */
export interface SessionInfo {
  name: string;
  commit: {
    id: string;
    message: string;
    author_name: string;
    author_email: string;
    created_at: string;
  };
  protected: boolean;
  can_push: boolean;
  web_url?: string;
}

/**
 * ActorGitManager設定インターフェース
 */
export interface ActorGitManagerConfig {
  /** GitLab設定 */
  gitlab: GitLabConfig;
  /** Embedding設定（モデル名など） */
  embedding?: {
    modelName?: string;
    dimensions?: number;
  };
  /** 作成者情報 */
  author: {
    name: string;
    email: string;
  };
}

/**
 * ActorGitManagerクラス
 * GitLabとGitEmbeddingSDKを統合したActor/Session管理システム
 */
export class ActorGitManager {
  private gitlabClient: GitLabClient;
  private config: ActorGitManagerConfig;
  private embeddingSDKs: Map<number, GitEmbeddingSDK> = new Map();

  constructor(config: ActorGitManagerConfig) {
    this.config = config;
    this.gitlabClient = new GitLabClient(config.gitlab);
  }

  /**
   * 新しいActor（プロジェクト）を作成
   * @param actorConfig Actor作成設定
   * @returns Actor情報
   */
  async createActor(actorConfig: CreateProjectConfig): Promise<ActorInfo> {
    try {
      const project = await this.gitlabClient.createActor(actorConfig);
      
      const actorInfo: ActorInfo = {
        id: project.id,
        name: project.name,
        description: project.description,
        clone_url: project.clone_url,
        web_url: project.web_url,
        default_branch: project.default_branch || 'main',
        created_at: project.created_at,
      };
      
      console.log(`Created new actor: ${actorInfo.name} (ID: ${actorInfo.id})`);
      return actorInfo;
    } catch (error) {
      console.error('Failed to create actor:', error);
      throw error;
    }
  }

  /**
   * Actorを取得
   * @param actorId Actor ID
   * @returns Actor情報
   */
  async getActor(actorId: number): Promise<ActorInfo> {
    try {
      const project = await this.gitlabClient.getActor(actorId);
      
      return {
        id: project.id,
        name: project.name,
        description: project.description,
        clone_url: project.clone_url,
        web_url: project.web_url,
        default_branch: project.default_branch || 'main',
        created_at: project.created_at,
        updated_at: project.updated_at,
      };
    } catch (error) {
      console.error('Failed to get actor:', error);
      throw error;
    }
  }

  /**
   * 新しいSession（ブランチ）を作成
   * @param actorId Actor ID
   * @param sessionConfig Session作成設定
   * @returns Session情報
   */
  async createSession(actorId: number, sessionConfig: CreateBranchConfig): Promise<SessionInfo> {
    try {
      const session = await this.gitlabClient.createSession(actorId, sessionConfig);
      
      const sessionInfo: SessionInfo = {
        name: session.name,
        commit: {
          id: String(session.commit.id),
          message: String(session.commit.message),
          author_name: String(session.commit.author_name),
          author_email: String(session.commit.author_email),
          created_at: String(session.commit.created_at),
        },
        protected: session.protected,
        can_push: session.can_push,
        web_url: session.web_url,
      };
      
      console.log(`Created new session: ${sessionInfo.name} for actor ${actorId}`);
      return sessionInfo;
    } catch (error) {
      console.error('Failed to create session:', error);
      throw error;
    }
  }

  /**
   * Sessionを取得
   * @param actorId Actor ID
   * @param sessionName Session名
   * @returns Session情報
   */
  async getSession(actorId: number, sessionName: string): Promise<SessionInfo> {
    try {
      const session = await this.gitlabClient.getSession(actorId, sessionName);
      
      return {
        name: session.name,
        commit: {
          id: String(session.commit.id),
          message: String(session.commit.message),
          author_name: String(session.commit.author_name),
          author_email: String(session.commit.author_email),
          created_at: String(session.commit.created_at),
        },
        protected: session.protected,
        can_push: session.can_push,
        web_url: session.web_url,
      };
    } catch (error) {
      console.error('Failed to get session:', error);
      throw error;
    }
  }

  /**
   * Actorの全Sessionを取得
   * @param actorId Actor ID
   * @returns Session一覧
   */
  async listSessions(actorId: number): Promise<SessionInfo[]> {
    try {
      const sessions = await this.gitlabClient.listSessions(actorId);
      
      return sessions.map(session => ({
        name: session.name,
        commit: {
          id: String(session.commit.id),
          message: String(session.commit.message),
          author_name: String(session.commit.author_name),
          author_email: String(session.commit.author_email),
          created_at: String(session.commit.created_at),
        },
        protected: session.protected,
        can_push: session.can_push,
        web_url: session.web_url,
      }));
    } catch (error) {
      console.error('Failed to list sessions:', error);
      throw error;
    }
  }

  /**
   * EmbeddingSDKを初期化
   * @param actorId Actor ID
   * @param branch ブランチ名（省略時はdefault_branch）
   * @returns GitEmbeddingSDK
   */
  async initializeEmbeddingSDK(actorId: number, branch?: string): Promise<GitEmbeddingSDK> {
    try {
      // 既に初期化済みの場合は再利用
      if (this.embeddingSDKs.has(actorId)) {
        const existingSDK = this.embeddingSDKs.get(actorId)!;
        
        // ブランチが指定されている場合は切り替え
        if (branch && existingSDK.getCurrentSession() !== branch) {
          await existingSDK.switchSession(branch);
        }
        
        return existingSDK;
      }

      // Actor情報を取得
      const actor = await this.getActor(actorId);
      const targetBranch = branch || actor.default_branch;

      // Git設定を作成
      const gitConfig: GitConfig = {
        url: actor.clone_url,
        token: this.config.gitlab.token,
        author: this.config.author,
        branch: targetBranch,
        dir: `/actor-${actorId}`,
      };

      // EmbeddingSDK設定を作成
      const embeddingConfig: GitEmbeddingConfig = {
        git: gitConfig,
        modelName: this.config.embedding?.modelName,
        dimensions: this.config.embedding?.dimensions,
      };

      // EmbeddingSDKを初期化
      const embeddingSDK = new GitEmbeddingSDK(embeddingConfig);
      await embeddingSDK.initialize();

      // キャッシュに保存
      this.embeddingSDKs.set(actorId, embeddingSDK);

      console.log(`Initialized EmbeddingSDK for actor ${actorId}, branch ${targetBranch}`);
      return embeddingSDK;
    } catch (error) {
      console.error('Failed to initialize EmbeddingSDK:', error);
      throw error;
    }
  }

  /**
   * ActorのEmbeddingSDKを取得（未初期化の場合は初期化）
   * @param actorId Actor ID
   * @param branch ブランチ名
   * @returns GitEmbeddingSDK
   */
  async getEmbeddingSDK(actorId: number, branch?: string): Promise<GitEmbeddingSDK> {
    return this.initializeEmbeddingSDK(actorId, branch);
  }

  /**
   * Actorにドキュメントを追加
   * @param actorId Actor ID
   * @param documents ドキュメント配列
   * @param sessionName セッション名（省略時は現在のセッション）
   * @returns 追加されたドキュメントのID配列
   */
  async addDocumentsToActor(
    actorId: number, 
    documents: Document[], 
    sessionName?: string
  ): Promise<string[]> {
    try {
      const embeddingSDK = await this.getEmbeddingSDK(actorId, sessionName);
      
      const documentIds = await embeddingSDK.addDocuments(documents);
      
      // リモートにプッシュ
      await embeddingSDK.push();
      
      console.log(`Added ${documentIds.length} documents to actor ${actorId}`);
      return documentIds;
    } catch (error) {
      console.error('Failed to add documents to actor:', error);
      throw error;
    }
  }

  /**
   * Actorで検索
   * @param actorId Actor ID
   * @param query 検索クエリ
   * @param k 返す結果数
   * @param sessionName セッション名（省略時は現在のセッション）
   * @returns 検索結果
   */
  async searchInActor(
    actorId: number, 
    query: string, 
    k: number = 5, 
    sessionName?: string
  ): Promise<any[]> {
    try {
      const embeddingSDK = await this.getEmbeddingSDK(actorId, sessionName);
      
      const results = await embeddingSDK.search(query, k);
      
      console.log(`Found ${results.length} results for query "${query}" in actor ${actorId}`);
      return results;
    } catch (error) {
      console.error('Failed to search in actor:', error);
      throw error;
    }
  }

  /**
   * ActorでSessionを作成し、EmbeddingSDKを初期化
   * @param actorId Actor ID
   * @param sessionName セッション名
   * @param baseBranch 基点となるブランチ（省略時はdefault_branch）
   * @returns GitEmbeddingSDK
   */
  async createActorSession(
    actorId: number, 
    sessionName: string, 
    baseBranch?: string
  ): Promise<GitEmbeddingSDK> {
    try {
      const actor = await this.getActor(actorId);
      const baseRef = baseBranch || actor.default_branch;

      // GitLabでブランチを作成
      await this.createSession(actorId, {
        name: sessionName,
        ref: baseRef,
      });

      // EmbeddingSDKを初期化してセッションを作成
      const embeddingSDK = await this.getEmbeddingSDK(actorId);
      await embeddingSDK.createSession(sessionName, baseRef);

      console.log(`Created session ${sessionName} for actor ${actorId}`);
      return embeddingSDK;
    } catch (error) {
      console.error('Failed to create actor session:', error);
      throw error;
    }
  }

  /**
   * ActorのSessionを切り替え
   * @param actorId Actor ID
   * @param sessionName セッション名
   * @returns GitEmbeddingSDK
   */
  async switchActorSession(actorId: number, sessionName: string): Promise<GitEmbeddingSDK> {
    try {
      const embeddingSDK = await this.getEmbeddingSDK(actorId);
      await embeddingSDK.switchSession(sessionName);

      console.log(`Switched to session ${sessionName} for actor ${actorId}`);
      return embeddingSDK;
    } catch (error) {
      console.error('Failed to switch actor session:', error);
      throw error;
    }
  }

  /**
   * Actorのファイルを保存
   * @param actorId Actor ID
   * @param filePath ファイルパス
   * @param content ファイル内容
   * @param commitMessage コミットメッセージ
   * @param sessionName セッション名
   */
  async saveFileToActor(
    actorId: number,
    filePath: string,
    content: string,
    commitMessage?: string,
    sessionName?: string
  ): Promise<string> {
    try {
      const embeddingSDK = await this.getEmbeddingSDK(actorId, sessionName);
      
      const commitSha = await embeddingSDK.saveFile(filePath, content, commitMessage);
      
      // リモートにプッシュ
      await embeddingSDK.push();
      
      console.log(`Saved file ${filePath} to actor ${actorId}`);
      return commitSha;
    } catch (error) {
      console.error('Failed to save file to actor:', error);
      throw error;
    }
  }

  /**
   * Actorからファイルを読み込み
   * @param actorId Actor ID
   * @param filePath ファイルパス
   * @param sessionName セッション名
   */
  async loadFileFromActor(
    actorId: number,
    filePath: string,
    sessionName?: string
  ): Promise<string> {
    try {
      const embeddingSDK = await this.getEmbeddingSDK(actorId, sessionName);
      
      const content = await embeddingSDK.loadFile(filePath);
      
      console.log(`Loaded file ${filePath} from actor ${actorId}`);
      return content;
    } catch (error) {
      console.error('Failed to load file from actor:', error);
      throw error;
    }
  }

  /**
   * Actorの状態を取得
   * @param actorId Actor ID
   * @param sessionName セッション名
   */
  async getActorStatus(actorId: number, sessionName?: string): Promise<{
    actor: ActorInfo;
    currentSession: string;
    sessions: SessionInfo[];
    indexSize: number;
    status: any[];
    commitHistory: any[];
  }> {
    try {
      const actor = await this.getActor(actorId);
      const sessions = await this.listSessions(actorId);
      const embeddingSDK = await this.getEmbeddingSDK(actorId, sessionName);

      const currentSession = embeddingSDK.getCurrentSession();
      const indexSize = await embeddingSDK.getIndexSize();
      const status = await embeddingSDK.getStatus();
      const commitHistory = await embeddingSDK.getCommitHistory();

      return {
        actor,
        currentSession,
        sessions,
        indexSize,
        status,
        commitHistory,
      };
    } catch (error) {
      console.error('Failed to get actor status:', error);
      throw error;
    }
  }

  /**
   * Actorを削除
   * @param actorId Actor ID
   */
  async deleteActor(actorId: number): Promise<boolean> {
    try {
      // EmbeddingSDKをクリーンアップ
      const embeddingSDK = this.embeddingSDKs.get(actorId);
      if (embeddingSDK) {
        await embeddingSDK.cleanup();
        this.embeddingSDKs.delete(actorId);
      }

      // GitLabプロジェクトを削除
      const result = await this.gitlabClient.deleteActor(actorId);
      
      console.log(`Deleted actor ${actorId}`);
      return result;
    } catch (error) {
      console.error('Failed to delete actor:', error);
      throw error;
    }
  }

  /**
   * Sessionを削除
   * @param actorId Actor ID
   * @param sessionName セッション名
   */
  async deleteSession(actorId: number, sessionName: string): Promise<boolean> {
    try {
      const result = await this.gitlabClient.deleteSession(actorId, sessionName);
      
      console.log(`Deleted session ${sessionName} from actor ${actorId}`);
      return result;
    } catch (error) {
      console.error('Failed to delete session:', error);
      throw error;
    }
  }

  /**
   * 全てのEmbeddingSDKをクリーンアップ
   */
  async cleanup(): Promise<void> {
    try {
      for (const [actorId, embeddingSDK] of this.embeddingSDKs) {
        await embeddingSDK.cleanup();
        console.log(`Cleaned up EmbeddingSDK for actor ${actorId}`);
      }
      
      this.embeddingSDKs.clear();
    } catch (error) {
      console.error('Failed to cleanup ActorGitManager:', error);
      throw error;
    }
  }
}

export default ActorGitManager; 